/*
  Hediye seçeneği: <gift-option> (işaretleme blocks/_gift-option.liquid'de).

  Görevi küçük tutuldu:
  - Kutucuğa göre alanları aç/kapa: `disabled` + `aria-expanded`. Disabled alanlar
    forma katılmaz; işaret kaldırılınca yazılmış not da gönderilmez.
  - Gönderilen veriden boş alıcı / not alanlarını çıkar (`formdata` olayı;
    product-form.js `new FormData(form)` kurduğunda tarayıcı bu olayı tetikler).
  - Not alanında kalan karakter sayacı.
  - Zorunlu alanlar: tarayıcının form doğrulaması (`required` + yalnızca boşluk
    için setCustomValidity). `invalid` olayında tarayıcı balonu yerine alanın
    altındaki hata metni gösteriliyor ve ilk hatalı alana odaklanılıyor.
    Horizon'un sepete ekle düğmesi `form.checkValidity()` geçmezse göndermiyor.
  - Sepete eklendikten sonra sıfırla.

  Tema modüllerine bağımlılık bilerek yok (ileride uygulama modülüne taşınabilsin);
  tek ortak nokta Shopify standart sepet olayının adı (CART_LINES_UPDATE_EVENT).
  Bu dosya layout/theme.liquid'den genel olarak yükleniyor: hızlı ekle penceresi
  ürün sayfasının HTML'ini morph ile içeri aldığında içindeki <script> etiketleri
  çalışmıyor.
*/

// @shopify/events: StandardEvents.cartLinesUpdate (Dwell 4.x). product-form-component
// bu olayı kendi üzerinde gönderiyor; sonuç `event.promise` ile geliyor ve başarılı
// eklemede `detail.source = 'product-form-component'`, `detail.didError = false`.
// (Dwell 3.x'te bunun yerine `cart:update` + `detail.data` kullanılıyordu.)
const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const QUICK_ADD_SELECTOR = '#quick-add-modal-content';
// Kalan karakter bu değerin altına inince ekran okuyucuya bir kez söylenir
const ANNOUNCE_THRESHOLD = 20;

class GiftOption extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {number | undefined} */
  #lastAnnounced;
  /** Aynı doğrulama turunda yalnızca ilk hatalı alana odaklanmak için */
  #focusedThisRound = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.closest(QUICK_ADD_SELECTOR) && this.dataset.showInQuickAdd === 'false') {
      this.hidden = true;
      this.#setFieldsDisabled(true);
      if (this.toggle) this.toggle.disabled = true;
      return;
    }

    this.#ensureUniqueIds();

    this.toggle?.addEventListener('change', () => this.#sync(), { signal });
    this.message?.addEventListener('input', () => this.#updateCounter(), { signal });

    for (const field of this.#textFields) {
      field.addEventListener('input', () => this.#validate(field, { clearOnly: true }), { signal });
    }
    // `invalid` kabarcıklanmaz; yakalama aşamasında dinle
    this.addEventListener('invalid', (event) => this.#onInvalid(event), { capture: true, signal });

    // Alıcı alanında Enter formu gönderip ürünü sepete eklemesin; nota geçsin
    this.recipient?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        this.message?.focus();
      },
      { signal }
    );

    this.#form = this.closest('form');
    this.#form?.addEventListener('formdata', (event) => this.#cleanFormData(event.formData), { signal });

    // Yalnızca bu formun bileşeninden gelen başarılı ekleme; sayfadaki başka
    // formlar (ör. hızlı ekle penceresi) bu bloğu sıfırlamasın.
    const productForm = this.closest('product-form-component') ?? this.#form;
    productForm?.addEventListener(
      CART_LINES_UPDATE_EVENT,
      (event) => {
        const promise = /** @type {Event & { promise?: Promise<{ detail?: any }> }} */ (event).promise;
        promise
          ?.then((result) => {
            const detail = result?.detail;
            if (signal.aborted || !detail || detail.source !== 'product-form-component' || detail.didError) return;
            this.reset();
          })
          .catch(() => {});
      },
      { signal }
    );

    this.#sync();
    this.#updateCounter();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get toggle() {
    return this.querySelector('[data-gift-option-toggle]');
  }

  /** @returns {HTMLInputElement | null} */
  get recipient() {
    return this.querySelector('[data-gift-option-recipient]');
  }

  /** @returns {HTMLTextAreaElement | null} */
  get message() {
    return this.querySelector('[data-gift-option-message]');
  }

  /** @returns {Array<HTMLInputElement | HTMLTextAreaElement>} */
  get fields() {
    return Array.from(this.querySelectorAll('[data-gift-option-field]'));
  }

  /** Alıcı ve not (fatura kutucuğu hariç) */
  get #textFields() {
    return [this.recipient, this.message].filter((field) => field !== null);
  }

  /** Kutucuğu ve alanları ilk haline döndürür */
  reset() {
    if (this.toggle) this.toggle.checked = false;
    for (const field of this.fields) {
      if (field instanceof HTMLInputElement && field.type === 'checkbox') {
        field.checked = false;
      } else {
        field.value = '';
      }
    }
    this.#lastAnnounced = undefined;
    this.#sync();
    this.#updateCounter();
  }

  #sync() {
    const checked = Boolean(this.toggle?.checked);
    this.toggle?.setAttribute('aria-expanded', String(checked));
    this.#setFieldsDisabled(!checked);
    for (const field of this.#textFields) {
      if (checked) {
        this.#validate(field, { clearOnly: true });
      } else {
        field.setCustomValidity('');
        this.#showError(field, '');
      }
    }
  }

  /**
   * Zorunlu alanda yalnızca boşluk da geçersiz. Hata metni yazarken yalnızca
   * siliniyor (clearOnly); ilk kez gönderimde (`invalid`) gösteriliyor.
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {{ clearOnly?: boolean }} [options]
   */
  #validate(field, { clearOnly = false } = {}) {
    if (!field.required || field.disabled) {
      field.setCustomValidity('');
      return;
    }
    const message = field.dataset.giftOptionError || '';
    field.setCustomValidity(field.value.trim() === '' ? message || ' ' : '');
    if (field.validity.valid) {
      this.#showError(field, '');
    } else if (!clearOnly) {
      this.#showError(field, message);
    }
  }

  /** @param {Event} event */
  #onInvalid(event) {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (!this.#textFields.includes(field)) return;

    // Tarayıcı balonu yerine alanın altındaki metin
    event.preventDefault();
    this.#validate(field);
    this.#showError(field, field.dataset.giftOptionError || field.validationMessage);

    if (!this.#focusedThisRound) {
      this.#focusedThisRound = true;
      // Mikro görev değil: tarayıcı her `invalid` olayından sonra mikro görevleri
      // çalıştırıyor, bayrak ikinci alandan önce sıfırlanırdı.
      setTimeout(() => (this.#focusedThisRound = false));
      field.focus({ preventScroll: true });
      field.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {string} message - Boşsa hata gizlenir
   */
  #showError(field, message) {
    // Hata öğesi aria-describedby üzerinden bulunuyor; id'ler kopya durumunda
    // yeniden adlandırılabildiği için (#ensureUniqueIds) addan türetilmiyor.
    const describedBy = (field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
    const error = describedBy
      .map((id) => this.querySelector(`[id="${CSS.escape(id)}"]`))
      .find((el) => el?.classList.contains('gift-option__error'));
    if (message) {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-invalid');
    }
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /** @param {boolean} disabled */
  #setFieldsDisabled(disabled) {
    for (const field of this.fields) field.disabled = disabled;
  }

  /**
   * Alan adları işaretlemeden okunuyor; ad tema ayarından geldiği için burada
   * sabit yok.
   * @param {FormData} formData
   */
  #cleanFormData(formData) {
    if (!this.toggle?.checked) {
      // Disabled alanlar zaten yok; kutucuk da işaretsiz. JS'in disabled'ı
      // kuramadığı bir an olursa diye yine de temizle.
      for (const field of this.fields) formData.delete(field.name);
      return;
    }

    for (const field of [this.recipient, this.message]) {
      if (!field || field.disabled) continue;
      const value = field.value.trim();
      if (value === '') {
        formData.delete(field.name);
      } else {
        formData.set(field.name, value);
      }
    }
  }

  #updateCounter() {
    const counter = this.querySelector('[data-gift-option-counter]');
    const message = this.message;
    if (!counter || !message) return;

    const max = Number(message.maxLength) || 0;
    // maxlength UTF-16 birimi sayar (emoji 2); sayaç da aynı ölçüyü kullanmalı
    const left = Math.max(0, max - message.value.length);
    const template = this.dataset.counterTemplate || '[count]';
    const text = template.replace('[count]', String(left));
    counter.textContent = text;

    // Her tuşta okumak yerine yalnızca sınıra yaklaşınca ve sınırda bir kez
    const live = this.querySelector('[data-gift-option-live]');
    if (!live) return;
    const bucket = left === 0 ? 0 : left <= ANNOUNCE_THRESHOLD ? ANNOUNCE_THRESHOLD : undefined;
    if (bucket !== undefined && bucket !== this.#lastAnnounced) {
      live.textContent = text;
    }
    this.#lastAnnounced = bucket;
  }

  /**
   * Ürün sayfasında aynı ürün hızlı ekle ile açılırsa aynı blok iki kez basılır
   * ve id'ler çakışır; o zaman bu kopyadaki id'leri ve onlara işaret eden
   * for / aria-* değerlerini yeniden adlandır.
   */
  #ensureUniqueIds() {
    const elementsWithId = Array.from(this.querySelectorAll('[id]'));
    const duplicate = elementsWithId.some((el) => document.querySelectorAll(`[id="${CSS.escape(el.id)}"]`).length > 1);
    if (!duplicate) return;

    const suffix = `-${Math.random().toString(36).slice(2, 8)}`;
    for (const el of elementsWithId) {
      const oldId = el.id;
      const newId = oldId + suffix;
      el.id = newId;
      for (const attr of ['for', 'aria-controls', 'aria-describedby']) {
        for (const ref of this.querySelectorAll(`[${attr}~="${CSS.escape(oldId)}"]`)) {
          const value = ref.getAttribute(attr) || '';
          ref.setAttribute(attr, value.split(' ').map((id) => (id === oldId ? newId : id)).join(' '));
        }
      }
    }
  }
}

if (!customElements.get('gift-option')) {
  customElements.define('gift-option', GiftOption);
}
